import React, {useCallback, useEffect, useRef, useState} from 'react';
import axios from 'axios';
import get from "lodash/get";

import useSearchStore from './_Global/SearchStore';

import AppliedFacetTag from './applied-facet-tag/jsx/AppliedFacetTag';
import Pagination from './pagination/jsx/Pagination';
import SearchSuggestions from './search-suggestions/jsx/SearchSuggestions';

import SearchFilters from './search-filters/SearchFilters';
import LoadingIndicator from './loading-indicator/LoadingIndicator';
import SearchResults from './search-results/SearchResults';
import SpellingSuggestion from './spelling-suggestion/SpellingSuggestion';
import {
  decodeString,
  getElementAbsoluteYPosition,
  getFacetOptionLabelByKey,
  getQueryParameterValuesByKey,
  toKebabCase,
  truncateString
} from './_Global/searchHelpers';

/**
 * Configuration for the search page REACT app.
 */
interface SearchConfiguration {
  inputHintText: string;
  inputLabelText: string;
  inputPlaceholderText: string;
  lookupUrl: string;
  searchPageType: SearchType;
  searchUrl: string;
  showHighlightedTerms: boolean;
  showRecentSearches: boolean;
  suggestionsUrl: string;
  summaryContentPath: string;
  summaryContentTruncation: number;
  title: string;
}

/**
 * Data required for generating the applied facet tags.
 */
interface SelectedFacetData {
    id: string;
    facetParamName: string;
    facetValue: string;
    label: string;
}

/**
 * Search results sorting options
 */
let SORTING_OPTIONS: SortingConfiguration[] = [];

const GLOBAL_SORTING_OPTIONS = [
    {
        label: 'Relevance', // Relevance/Default
        value: '',
    },
    {
        label: 'Title: A-Z',
        value: 'title',
    },
    {
        label: 'Title: Z-A',
        value: 'dtitle',
    },
];

const PEOPLE_SORTING_OPTIONS = [
    {
        label: 'Name: A-Z',
        value: 'metanameLast',
    },
    {
        label: 'Job Title: A-Z',
        value: 'metasortKey',
    },
    {
        label: 'Location',
        value: 'metalocation',
    },
];

const INSIGHTS_SORTING_OPTIONS = [
    {
        label: 'Relevance', // Relevance/Default
        value: '',
    },
    {
        label: 'A-Z Title',
        value: 'title',
    },
    {
        label: 'Published Date',
        value: 'date',
    },
];

const NEWS_SORTING_OPTIONS = [
    {
        label: 'Relevance', // Relevance/Default
        value: '',
    },
    {
        label: 'A-Z Title',
        value: 'title',
    },
    {
        label: 'Published Date',
        value: 'date',
    },
];

const JOBS_SORTING_OPTIONS = [
    {
        label: 'Relevance', // Relevance/Default
        value: '',
    },
    {
        label: 'A-Z Role Title',
        value: 'metaroleTitle',
    },
];

/**
 * Search results per page options.
 * Used for the input that changes the num_ranks parameter.
 */
let DEFAULT_SORT_ORDER = '';

const GLOBAL_SORT_ORDER = '';
const PEOPLE_SORT_ORDER = 'metasortKey';
const INSIGHTS_SORT_ORDER = 'date';
const NEWS_SORT_ORDER = 'date';
const JOBS_SORT_ORDER = '';

/**
 * Search results per page options.
 * Used for the input that changes the num_ranks parameter.
 */
let RESULTS_PER_PAGE_OPTIONS = 10;

const GLOBAL_PER_PAGE_OPTIONS = 16;
const PEOPLE_PER_PAGE_OPTIONS = 16;
const INSIGHTS_PER_PAGE_OPTIONS = 16;
const NEWS_PER_PAGE_OPTIONS = 16;
const JOBS_PER_PAGE_OPTIONS = 10;

/**
 * Search Facet accordions.
 * These will be updated when the component mounts
 * so that we can set the Facets based on the
 * search page type.
 *
 * Note: When adding new Facets, make sure to also
 * update the Funnelback URL Builder in Matrix so
 * that the REST Asset will allow them to be used.
 */
let SEARCH_FACET_OPTIONS: SearchFacetConfiguration[] = [];

const GLOBAL_SEARCH_FACETS: SearchFacetConfiguration[] = [
    {
        name: 'Type',
        queryStringParamName: 'f.Type|type',
        urlEncodedFormat: 'f.Type%7Ctype',
        type: 'checkbox',
        showResultCount: false,
    },
    {
        name: 'Service',
        queryStringParamName: 'f.Service|service',
        urlEncodedFormat: 'f.Service%7Cservice',
        type: 'checkbox',
        showResultCount: false,
    },
    {
        name: 'Sector',
        queryStringParamName: 'f.Sector|sector',
        urlEncodedFormat: 'f.Sector%7Csector',
        type: 'checkbox',
        showResultCount: false,
    },
];

const PEOPLE_SEARCH_FACETS: SearchFacetConfiguration[] = [
    {
        name: 'Job Title',
        queryStringParamName: 'f.Job Title|role',
        urlEncodedFormat: 'f.Job+Title%7Crole',
        type: 'checkbox',
        showResultCount: false,
        customOrder: ['Partner', 'Special Counsel', 'Lawyer', 'Business Professional'],
    },
    {
        name: 'Service',
        queryStringParamName: 'f.Service|service',
        urlEncodedFormat: 'f.Service%7Cservice',
        type: 'checkbox',
        showResultCount: false,
    },
    {
        name: 'Sector',
        queryStringParamName: 'f.Sector|sector',
        urlEncodedFormat: 'f.Sector%7Csector',
        type: 'checkbox',
        showResultCount: false,
    },
    {
        name: 'Location',
        queryStringParamName: 'f.Location|location',
        urlEncodedFormat: 'f.Location%7Clocation',
        type: 'checkbox',
        showResultCount: false,
    },
];

const INSIGHTS_SEARCH_FACETS: SearchFacetConfiguration[] = [
    {
        name: 'Sector',
        queryStringParamName: 'f.Sector|sector',
        urlEncodedFormat: 'f.Sector%7Csector',
        type: 'checkbox',
        showResultCount: false,
    },
    {
        name: 'Service',
        queryStringParamName: 'f.Service|service',
        urlEncodedFormat: 'f.Service%7Cservice',
        type: 'checkbox',
        showResultCount: false,
    },
    {
        name: 'Year',
        queryStringParamName: 'f.Year|year',
        urlEncodedFormat: 'f.Year%7Cyear',
        type: 'checkbox',
        showResultCount: false,
    },
];

const NEWS_SEARCH_FACETS: SearchFacetConfiguration[] = [
    {
        name: 'Sector',
        queryStringParamName: 'f.Sector|sector',
        urlEncodedFormat: 'f.Sector%7Csector',
        type: 'checkbox',
        showResultCount: false,
    },
    {
        name: 'Service',
        queryStringParamName: 'f.Service|service',
        urlEncodedFormat: 'f.Service%7Cservice',
        type: 'checkbox',
        showResultCount: false,
    },
    {
        name: 'Year',
        queryStringParamName: 'f.Year|year',
        urlEncodedFormat: 'f.Year%7Cyear',
        type: 'checkbox',
        showResultCount: false,
    },
];

const JOBS_SEARCH_FACETS: SearchFacetConfiguration[] = [
    {
        name: 'Team',
        queryStringParamName: 'f.Team|team',
        urlEncodedFormat: 'f.Team%7Cteam',
        type: 'checkbox',
        showResultCount: false,
    },
    {
        name: 'Role Type',
        queryStringParamName: 'f.Role Type|roleType',
        urlEncodedFormat: 'f.Role+Type%7CroleType',
        type: 'checkbox',
        showResultCount: false,
    },
    {
        name: 'Location',
        queryStringParamName: 'f.Location|location',
        urlEncodedFormat: 'f.Location%7Clocation',
        type: 'checkbox',
        showResultCount: false,
    },
];

/**
 * Search Facet Constant filter value.
 * This won't be updated, but be a constant filter
 * to apply to every search.
 *
 * Note: When adding new Facets, make sure to also
 * update the Funnelback URL Builder in Matrix so
 * that the REST Asset will allow them to be used.
 */
let SEARCH_FACET_CONSTANT: SearchFacetSelections = {};

const GLOBAL_FACET_CONSTANT: SearchFacetSelections = {};
const PEOPLE_FACET_CONSTANT: SearchFacetSelections = {};
const INSIGHTS_FACET_CONSTANT: SearchFacetSelections = {
    'f.Type|type': ['insights'],
};
const NEWS_FACET_CONSTANT: SearchFacetSelections = {
    'f.Type|type': ['news'],
};
const JOBS_FACET_CONSTANT: SearchFacetSelections = {};

/**
 * Controllers for cancelling pending XHR requests.
 *
 * @source https://www.npmjs.com/package/axios#cancellation
 */
let funnelbackSearchRequestController: AbortController | null = null;
let funnelbackSuggestionRequestController: AbortController | null = null;

export default function App({
  inputHintText,
  inputLabelText,
  inputPlaceholderText,
  lookupUrl,
  searchPageType,
  searchUrl,
  showHighlightedTerms,
  showRecentSearches,
  suggestionsUrl,
  summaryContentPath,
  summaryContentTruncation,
  title,
}: SearchConfiguration) {
    const searchPageURL = searchUrl;
    const searchPageTitle = title;

    const searchMainContainer = useRef<HTMLDivElement>(null);
    const searchQueryArea = useRef<HTMLDivElement>(null);

    const [searchResultsJson, setSearchResultsJson] = useState<SearchResult[]>([]);
    const [searchHasNoResults, setSearchHasNoResults] = useState(false);
    const [isRequestingSearchResults, setIsRequestingSearchResults] = useState(false);
    const [isRequestingSearchSuggestions, setIsRequestingSearchSuggestions] = useState(false);
    const [initialSearch, setInitialSearch] = useState(true);

    const setDefaultSortOrder = useSearchStore((state) => state.setDefaultSortOrder);

    const setDefaultResultsPerPage = useSearchStore((state) => state.setDefaultResultsPerPage);

    const userSearchTerm = useSearchStore((state) => state.userSearchTerm);

    const setUserSearchTerm = useSearchStore((state) => state.setUserSearchTerm);

    const funnelbackQueryParameters = useSearchStore((state) => state.funnelbackQueryParameters);

    const setFunnelbackQueryParameters = useSearchStore((state) => state.setFunnelbackQueryParameters);

    const resetFunnelbackQueryParameters = useSearchStore((state) => state.resetFunnelbackQueryParameters);

    const resultsSummary = useSearchStore((state) => state.resultsSummary);

    const setResultsSummary = useSearchStore((state) => state.setResultsSummary);

    const searchFacetsJson = useSearchStore((state) => state.searchFacetsJson);

    const setSearchSuggestions = useSearchStore((state) => state.setSearchSuggestions);

    const setSearchFacetsJson = useSearchStore((state) => state.setSearchFacetsJson);

    const searchFacetSelections = useSearchStore((state) => state.searchFacetSelections);

    const setBatchSearchFacetSelectionsByKey = useSearchStore((state) => state.setBatchSearchFacetSelectionsByKey);

    const setRecentSearches = useSearchStore((state) => state.setRecentSearches);

    const queueScrollToForm = useSearchStore((state) => state.queueScrollToForm);

    const setQueueScrollToForm = useSearchStore((state) => state.setQueueScrollToForm);

    const spellingSuggestion = useSearchStore((state) => state.spellingSuggestion);

    const setSpellingSuggestion = useSearchStore((state) => state.setSpellingSuggestion);

    const setDefaultFacetSelection = useSearchStore((state) => state.setDefaultFacetSelection);

    const setSearchTermHighlighting = useSearchStore((state) => state.setSearchTermHighlighting);

    /**
     * Respond to keyboard presses within the context of the search form
     *
     * @param {KeyboardEvent} event Event that triggered the click
     *
     * @returns void
     */
    const onKeyPress = useCallback((event) => {
        if (event.key === 'Escape') {
            event.stopPropagation();
            // Close the suggestions list
            setSearchSuggestions([]);
        }
    }, []);

    /**
     * Operations to perform when the search component first loads.
     */
    useEffect(() => {
        // Set the search Facets to use
        switch (searchPageType) {
            case 'people':
                SEARCH_FACET_OPTIONS = [...PEOPLE_SEARCH_FACETS];
                SEARCH_FACET_CONSTANT = PEOPLE_FACET_CONSTANT;
                SORTING_OPTIONS = [...PEOPLE_SORTING_OPTIONS];
                DEFAULT_SORT_ORDER = PEOPLE_SORT_ORDER;
                RESULTS_PER_PAGE_OPTIONS = PEOPLE_PER_PAGE_OPTIONS;
                RESULTS_PER_PAGE_OPTIONS = PEOPLE_PER_PAGE_OPTIONS;
                break;
            case 'insights':
                SEARCH_FACET_OPTIONS = [...INSIGHTS_SEARCH_FACETS];
                SEARCH_FACET_CONSTANT = INSIGHTS_FACET_CONSTANT;
                SORTING_OPTIONS = [...INSIGHTS_SORTING_OPTIONS];
                DEFAULT_SORT_ORDER = INSIGHTS_SORT_ORDER;
                RESULTS_PER_PAGE_OPTIONS = INSIGHTS_PER_PAGE_OPTIONS;
                break;
            case 'jobs':
                SEARCH_FACET_OPTIONS = [...JOBS_SEARCH_FACETS];
                SEARCH_FACET_CONSTANT = JOBS_FACET_CONSTANT;
                SORTING_OPTIONS = [...JOBS_SORTING_OPTIONS];
                DEFAULT_SORT_ORDER = JOBS_SORT_ORDER;
                RESULTS_PER_PAGE_OPTIONS = JOBS_PER_PAGE_OPTIONS;
                break;
            case 'news':
                SEARCH_FACET_OPTIONS = [...NEWS_SEARCH_FACETS];
                SEARCH_FACET_CONSTANT = NEWS_FACET_CONSTANT;
                SORTING_OPTIONS = [...NEWS_SORTING_OPTIONS];
                DEFAULT_SORT_ORDER = NEWS_SORT_ORDER;
                RESULTS_PER_PAGE_OPTIONS = NEWS_PER_PAGE_OPTIONS;
                break;
            default:
                // Global by default
                SEARCH_FACET_OPTIONS = [...GLOBAL_SEARCH_FACETS];
                SEARCH_FACET_CONSTANT = GLOBAL_FACET_CONSTANT;
                SORTING_OPTIONS = [...GLOBAL_SORTING_OPTIONS];
                DEFAULT_SORT_ORDER = GLOBAL_SORT_ORDER;
                RESULTS_PER_PAGE_OPTIONS = GLOBAL_PER_PAGE_OPTIONS;
                break;
        }

        // Set default sort order options
        setDefaultSortOrder(DEFAULT_SORT_ORDER);

        // Set default per page options
        setDefaultResultsPerPage(RESULTS_PER_PAGE_OPTIONS);

        // Set facet constant
        setDefaultFacetSelection(SEARCH_FACET_CONSTANT);

        // Set the configuration for highlighting search terms
        setSearchTermHighlighting(showHighlightedTerms);

        /**
         * Check the page query parameters for any search query
         * terms to "pre-apply" to the search results.
         */
        const queryString = window.location.href.split('?')[1] || '';
        const foundFacetQueryParameters: FacetQueryParameter[] = [];
        const foundQueryParameters: FunnelbackQueryParameters = {};
        let foundSearchTermQueryParameters: string[] = [];
        let providedSearchTerm = '';
        let foundSortQueryParameters: string[] = [];
        let foundRanksQueryParameters: string[] = [];
        let foundJobAdIdQueryParameters: string[] = [];
        let queryStringKeyValues: string[] = [];

        if (queryString !== '') {
            // Loop over the possible facet options
            for (let i = 0, j = SEARCH_FACET_OPTIONS.length; i < j; ++i) {
                queryStringKeyValues = getQueryParameterValuesByKey(queryString, SEARCH_FACET_OPTIONS[i].urlEncodedFormat);

                if (queryStringKeyValues.length !== 0) {
                    // Capture the query key and it's value(s)
                    foundFacetQueryParameters.push({
                        queryStringParamName: SEARCH_FACET_OPTIONS[i].queryStringParamName,
                        queryStringParamValue: queryStringKeyValues,
                    });
                }
            }

            // Check for a provided search term
            foundSearchTermQueryParameters = getQueryParameterValuesByKey(queryString, 'query');

            // Check for a provided sort option
            foundSortQueryParameters = getQueryParameterValuesByKey(queryString, 'sort');

            // Check for a provided number of results
            foundRanksQueryParameters = getQueryParameterValuesByKey(queryString, 'num_ranks');

            // Check for a provided Job Ad ID for the careers search
            foundJobAdIdQueryParameters = getQueryParameterValuesByKey(queryString, 'meta_adId');
        }

        if (foundFacetQueryParameters.length !== 0) {
            // Update the shared state with the found facet value(s)
            setBatchSearchFacetSelectionsByKey(foundFacetQueryParameters);
        }

        if (foundSearchTermQueryParameters.length !== 0 && foundSearchTermQueryParameters[0] !== '') {
            providedSearchTerm = foundSearchTermQueryParameters[0].replace(/\+/g, ' ');

            // Capture the provided search term
            foundQueryParameters['query'] = providedSearchTerm;

            // Update the search input area with the provided search term
            setUserSearchTerm(decodeURIComponent(providedSearchTerm));
        }

        if (foundSortQueryParameters.length !== 0) {
            // Capture the provided sorting value
            foundQueryParameters['sort'] = foundSortQueryParameters[0];
        }

        if (foundRanksQueryParameters.length !== 0) {
            // Capture the provided sorting value
            foundQueryParameters['num_ranks'] = parseInt(foundRanksQueryParameters[0], 10);
        }

        if (foundJobAdIdQueryParameters.length !== 0) {
          // Capture the provided Job ID value
          foundQueryParameters['meta_adId'] = parseInt(foundJobAdIdQueryParameters[0], 10);
          // Also add "full matches only" the query
          foundQueryParameters['fmo'] = true;
        }

        // Update the global state with the provided query parameters (if any)
        if (Object.keys(foundQueryParameters).length !== 0) {
            setFunnelbackQueryParameters(foundQueryParameters, false);
        }

        // Add an Event Listener to catch any key presses (scoped to the search area)
        if (searchQueryArea.current !== null) {
            searchQueryArea.current.addEventListener('keydown', onKeyPress, false);
        }

        return () => {
            // Remove the listeners when component removed from DOM
            if (searchQueryArea.current !== null) {
                searchQueryArea.current.removeEventListener('keydown', onKeyPress, false);
            }
        };
    }, []);

    /**
     * Operations to perform when the Funnelback query is updated.
     *
     * e.g. when the sort option has changed, a pagination button
     * was clicked, or when the Facet selections have changed.
     */
    useEffect(() => {
        // Make the search request to update the search content
        getSearchResults();
    }, [funnelbackQueryParameters]);

    // Testing state changes
    // useEffect(() => {
    //     console.log('Search results updated:', searchResultsJson);
    // }, [searchResultsJson]);
    // useEffect(() => {
    //     console.log('Search facets updated:', searchFacetsJson);
    // }, [searchFacetsJson]);

    /**
     * Respond to changes to the resources search input
     *
     * @param {Change Event} evt Event that triggered the input to update
     *
     * @returns void
     */
    const onSearchInputChange = (evt) => {
        // Trim any leading whitespace
        const inputValue = evt.target.value.replace(/^\s+/, '');

        // Capture the key press
        setUserSearchTerm(inputValue);

        // Cancel any pending suggestions request
        if (funnelbackSuggestionRequestController !== null) {
            funnelbackSuggestionRequestController.abort();
        }

        // Hide input loading indicator
        setIsRequestingSearchSuggestions(false);

        // Reset the search suggestions
        setSearchSuggestions([]);

        if (inputValue.length >= 2) {
            // Request search suggestions
            // eslint-disable-next-line no-use-before-define
            getSearchSuggestions(inputValue);
        }
    };

    /**
     * Respond to clicks on button that clears the search input.
     *
     * @param {Click Event} evt Event that triggered the click
     *
     * @returns void
     */
    const onClearSearchButtonClick = (evt) => {
        evt.preventDefault();

        // Reset the search input area
        setUserSearchTerm('');

        // Reset the search suggestions
        setSearchSuggestions([]);

        // Trigger the search results to refresh
        resetFunnelbackQueryParameters();
    };

    /**
     * Respond to attempts to submit the search form.
     *
     * This is mostly used to capture when a user
     * submits a search query (even if it's empty).
     *
     * @param {Submit Event} evt Event that triggered the submission
     *
     * @returns void
     */
    const onFormSubmit = (evt) => {
        evt.preventDefault();

        // Cancel any pending suggestions request
        if (funnelbackSuggestionRequestController !== null) {
            funnelbackSuggestionRequestController.abort();
        }

        // Hide input loading indicator
        setIsRequestingSearchSuggestions(false);

        // Reset the search suggestions
        setSearchSuggestions([]);

        // Take the current search term and request results
        // Escape some characters first
        setFunnelbackQueryParameters({
            query: userSearchTerm.trim().replaceAll(/%/g, "%25"),
        });
    };

    /**
     * Make a request to Funnelback (via Matrix proxy) to get
     * the search results for the currently set search term
     * and facets.
     *
     * @returns void
     */
    function getSearchResults() {
        const storedQueryParameters = {
            ...funnelbackQueryParameters,
        };
        const storedFacetSelections = {
            ...searchFacetSelections,
        };
        const fullQueryString: string[] = [];
        const currentPageUrl = window.location.href.split('?')[0];
        let encodedQueryParameters = '';

        // Cancel any pending request for search results
        if (funnelbackSearchRequestController !== null) {
            funnelbackSearchRequestController.abort();
        }

        if (storedQueryParameters.query && initialSearch) {
            // Disable initial loading if the page is loading for the first time with a query
            setInitialSearch(false);
        }

        // Hide the no results message
        setSearchHasNoResults(false);

        // Show the loading message
        setIsRequestingSearchResults(true);

        // Create a new controller instance
        funnelbackSearchRequestController = new AbortController();

        // Combine the standard key/value pairs into a single query string
        Object.entries(storedQueryParameters).forEach((queryPair) => {
            const queryKey = queryPair[0];
            const queryValue = queryPair[1];

            fullQueryString.push(`${queryKey}=${queryValue}`);
        });

        // Facets will be an Object with query keys and an Array of value(s)
        Object.entries(storedFacetSelections).forEach((facetQueryPair) => {
            const facetKey = facetQueryPair[0];
            const facetValues = facetQueryPair[1];

            for (let i = 0, j = facetValues.length; i < j; ++i) {
                fullQueryString.push(`${facetKey}=${facetValues[i]}`);
            }
        });

        /**
         * Capture the current search parameters so that
         * we can update the browser history after a
         * successful request.
         */
        encodedQueryParameters = encodeURI(fullQueryString.join('&')).replaceAll(/%20/g, '+').replaceAll(/%2525/g, '%25');

        axios
            .get(`${lookupUrl}&${fullQueryString.join('&')}`, {
                signal: funnelbackSearchRequestController.signal,
            })
            .then((response) => {
                const responseData = response.data.response;

                // Clear the request controller
                funnelbackSearchRequestController = null;

                // Capture the search results
                setSearchResultsJson(
                  parseSearchResults(responseData.resultPacket.results, storedQueryParameters.query || '', summaryContentPath)
                );

                // Capture the search facets
                setSearchFacetsJson(parseSearchFacets(responseData.facets));

                // Capture the results summary
                setResultsSummary({
                    fullyMatching: responseData.resultPacket.resultsSummary.fullyMatching,
                    partiallyMatching: responseData.resultPacket.resultsSummary.partiallyMatching,
                    totalMatching: responseData.resultPacket.resultsSummary.totalMatching,
                    currentSetStart: responseData.resultPacket.resultsSummary.currStart,
                    currentSetEnd: responseData.resultPacket.resultsSummary.currEnd,
                    prevStart: responseData.resultPacket.resultsSummary.prevStart,
                    nextStart: responseData.resultPacket.resultsSummary.nextStart,
                });

                // Capture spelling suggestion
                setSpellingSuggestion(responseData.resultPacket.spell?.text);

                // Hide the loading message
                setIsRequestingSearchResults(false);

                // Disable initial loading to prevent placeholder cards after first search
                setInitialSearch(false);

                // If there are no results, show the no results message
                if (responseData.resultPacket.results.length === 0) {
                    setSearchHasNoResults(true);
                } else {
                    /**
                     * As we have a successful search response and
                     * there are results to show, update the recent
                     * searches list.
                     */
                    setRecentSearches(storedQueryParameters['query'] || '');
                }

                /**
                 * Replace the current page URL so that it includes the
                 * applied search parameters.
                 *
                 * This is so that search query is "remembered" if the user
                 * navigates away from this page and then back again.
                 */
                window.history.replaceState({}, '', `${currentPageUrl}?${encodedQueryParameters}`);

                /**
                 * Scroll the search form to the top, as requested.
                 */
                if (queueScrollToForm) {
                    // Scroll the page
                    window.scrollTo({
                        top: getElementAbsoluteYPosition(searchMainContainer.current),
                        left: 0,
                        behavior: 'instant',
                    });

                    // Reset the queue
                    setQueueScrollToForm(false);
                }
            })
            .catch((error) => {
                if (error.code === 'ERR_CANCELED') {
                    // The request was cancelled, do nothing.
                    return;
                }

                // Show the no results message
                setSearchHasNoResults(true);

                // Hide the loading message
                setIsRequestingSearchResults(false);
            });
    }

    /**
     * Given the facet data JSON response from Funnelback,
     * parse it into a modified form that we use for the
     * search application.
     *
     * @param {Array} funnelbackFacetData
     * The facet data from the search response JSON
     *
     * @returns
     * Facet data that can be stored in the shared app state
     */
    function parseSearchFacets(funnelbackFacetData: FunnelbackSearchRawFacetData[]) {
        const parsedSearchFacets: SearchFacet[] = [];

        // Loop over any facet data
        for (let i = 0, j = funnelbackFacetData.length; i < j; ++i) {
            // Loop over the possible facet options
            for (let f = 0, w = SEARCH_FACET_OPTIONS.length; f < w; ++f) {
                if (funnelbackFacetData[i].name === SEARCH_FACET_OPTIONS[f].name) {
                    // Matching facet found
                    let newFacet: SearchFacet = {
                        name: SEARCH_FACET_OPTIONS[f].name,
                        queryStringParamName: SEARCH_FACET_OPTIONS[f].queryStringParamName,
                        allValues: [],
                        selectedValues: [],
                        customOrder: SEARCH_FACET_OPTIONS[f].customOrder ?? [],
                    };

                    // Loop over all the facet options
                    for (let x = 0, q = funnelbackFacetData[i].allValues.length; x < q; ++x) {
                        let newFacetValue: SearchFacetValue = {
                            value: funnelbackFacetData[i].allValues[x].data.replace(/ /g, '+'),
                            label: getFacetOptionLabelByKey(funnelbackFacetData[i].allValues[x].label),
                            count: funnelbackFacetData[i].allValues[x].count,
                        };

                        newFacet.allValues.push(newFacetValue);

                        // Check if the option is currently selected
                        if (funnelbackFacetData[i].allValues[x].selected) {
                            // Add the option to the selected values as well
                            newFacet.selectedValues.push(newFacetValue);
                        }
                    }

                    // Capture the parsed facet
                    parsedSearchFacets.push(newFacet);

                    // Skip to the next facet data
                    break;
                }
            }
        }

        return parsedSearchFacets;
    }

    /**
     * Given the result(s) data JSON response from Funnelback,
     * parse it into a modified form that we use for the
     * search application.
     *
     * @param {Array} funnelbackResultsData
     * The results data from the search response JSON
     * @param {String} searchTerm
     * The search term submitted for the current results
     * @param {String} summaryContentPath
     * The results JSON key for the content to highlight
     *
     * @returns
     * Results data with additional keys and values based on the
     * application configuration
     */
    function parseSearchResults(funnelbackResultsData: SearchResult[], searchTerm: string, summaryContentPath: string) {
      const parsedSearchResults: SearchResult[] = [...funnelbackResultsData];

      /**
       * Prepare the search term for highlighting matched terms
       * in the result description - if that has been enabled
       * in the application configuration.
       *
       * Escape the percentage symbol as it may be used in the
       * search term.
       * Check if the submitted a exact search query, highlight
       * that exact term.
       * Otherwise split the search terms and highlight each of
       * them to better match the Funnelback search
       *
       * There are a whole host of improvements and test cases
       * we could make from this doc:
       * https://docs.squiz.net/funnelback/docs/latest/use/query-language-help.html
       *
       * One obvious improvement is to use the
       * response.resultPacket.queryAsProcessed value in the
       * Funnelback response to match on the same terms Funnelback did.
       * e.g. words like "and", "or" are not actually used in the
       * search unless it's an exact search so it's misleading to
       * highlight them in our results.
       */
      const exactTermMatch = new RegExp(/^".*?"$/);
      let escapedSearchTermMatch = searchTerm;

      // Unescape the percentage symbol
      escapedSearchTermMatch = escapedSearchTermMatch.replaceAll('%25', "%");

      if (searchTerm.match(exactTermMatch)) {
        // Remove double quotes
        escapedSearchTermMatch = escapedSearchTermMatch.replaceAll('"', '');
      } else {
        // Replace whitespace with a pipe
        escapedSearchTermMatch = escapedSearchTermMatch.replaceAll(/\s/g, '|');
      }

      // Truncate the search result summary, as per app configuration
      let resultSummary = '';

      if (summaryContentTruncation > 0) {
        for (let k = 0, q = parsedSearchResults.length; k < q; ++k) {
          resultSummary = get(parsedSearchResults[k], summaryContentPath, '');

          if (resultSummary !== '') {
            resultSummary = truncateString( decodeString(resultSummary), summaryContentTruncation );
          }

          // Set the content
          parsedSearchResults[k].summaryTruncated = resultSummary;
        }
      }

      /**
       * Special case for the Gilbert + Tobin project.
       *
       * Add the plus (+) for the company name that is
       * automatically removed in the result summary
       * by Funnelback.
       *
       * This is a known issue specifically with how
       * Funnelback extracts a summary from document content.
       *
       * NOTE:
       * We assume here that we are always using the
       * truncated summary for the result.
       *
       * e.g.
       * Add a plus:
       * "member of GT Innovate" > "member of G+T Innovate"
       * "benefiting GT's practice" > "benefiting G+T's practice"
       * "any better than GT" > "get any better than G+T"
       *
       * Add spaces and a plus:
       * "owned by Gilbert Tobin." > "owned by Gilbert + Tobin."
       *
       * Do NOT add a plus:
       * "Paralegals and GTDOCs Review Analysts"
       */
      const gtAcronym = new RegExp(/GT(?![A-z])/, 'gm');
      const gtTitle = new RegExp(/Gilbert\s*Tobin/, 'gm');
      let resultSummaryGT = '';

      for (let x = 0, z = parsedSearchResults.length; x < z; ++x) {
        resultSummaryGT = parsedSearchResults[x].summaryTruncated || '';

        if (resultSummaryGT !== '') {
          resultSummaryGT = resultSummaryGT.replaceAll(gtAcronym, 'G+T');
          resultSummaryGT = resultSummaryGT.replaceAll(gtTitle, 'Gilbert + Tobin');
          parsedSearchResults[x].summaryTruncated = resultSummaryGT;
        }
      }

      /**
       * We also want to escape the + character in the search
       * term for the highlighting to work as expected.
       */
      escapedSearchTermMatch = escapedSearchTermMatch.replaceAll('+', '\\+');
      // End: Gilbert + Tobin project specific fix.

      /**
       * Check if highlighting is enabled and there is a
       * search term to match on.
       *
       * Update the results JSON to add the
       * "summaryHighlighted" key and HTML value
       */
      const searchTermMatch = new RegExp(escapedSearchTermMatch, 'gi');
      let highlightedSummary = '';
      let uniqueMatches: string[] = [];

      if (summaryContentPath !== '') {
        for (let i = 0, j = parsedSearchResults.length; i < j; ++i) {
          // Use the truncated summary, if enabled in the application config.
          highlightedSummary = parsedSearchResults[i].summaryTruncated || decodeString(parsedSearchResults[i].summary);

          if (
              highlightedSummary !== '' &&
              searchTerm !== ''
            ) {
            // Get a list of unique search term matches (e.g. different case sensitivity, de-duplicated)
            uniqueMatches = [...new Set(highlightedSummary.match(searchTermMatch))];

            // Replace each match
            uniqueMatches.forEach(matchedTerm => {
              // Wrap the matched term with markup
              highlightedSummary = highlightedSummary.replaceAll(matchedTerm, `{{{${matchedTerm}}}}`);
            });

            // Avoid accidentally replacing the markup code by swapping out the placeholder last
            highlightedSummary = highlightedSummary.replaceAll('{{{', '<span class="term-highlighted">');
            highlightedSummary = highlightedSummary.replaceAll('}}}', '</span>');
          }

          // Set the content
          parsedSearchResults[i].summaryHighlighted = highlightedSummary;
        }
      }

      return parsedSearchResults;
    }

    /**
     * Make a request to Funnelback (via Matrix proxy) to get
     * search suggestions based on a provided partial query
     *
     * @param {String} partialQuery The search text to find suggestions for
     *
     * @returns void
     */
    function getSearchSuggestions(partialQuery) {
        const fullQueryString: string[] = [];
        // Show input loading indicator
        setIsRequestingSearchSuggestions(true);

        // Create a new controller instance
        funnelbackSuggestionRequestController = new AbortController();

        // Add the partial search to the request
        fullQueryString.push(`query=${partialQuery}`);

        axios
            .get(`${suggestionsUrl}?${fullQueryString.join('&')}`, {
                signal: funnelbackSuggestionRequestController.signal,
            })
            .then((response) => {
                const responseData = response.data;
                const parsedSuggestions: SearchSuggestion[] = [];

                // Clear the request controller
                funnelbackSuggestionRequestController = null;

                // Check if the response contains suggestions
                if (Object.prototype.toString.call(responseData) !== '[object Array]') {
                    // Hide the loading message
                    setIsRequestingSearchSuggestions(false);

                    // Error in the suggestions request
                    return;
                }

                for (let i = 0, j = responseData.length; i < j; ++i) {
                    parsedSuggestions.push({
                        id: `suggestion-${Math.ceil(Math.random() * 10000)}`,
                        label: responseData[i].disp,
                        value: responseData[i].disp,
                    });
                }

                // Update the local state with any suggestions
                if (parsedSuggestions.length !== 0) {
                    setSearchSuggestions(parsedSuggestions);
                }

                // Hide the loading message
                setIsRequestingSearchSuggestions(false);
            })
            .catch((error) => {
                if (error.code === 'ERR_CANCELED') {
                    // The request was cancelled, do nothing.
                    return;
                }

                // Hide the loading message
                setIsRequestingSearchSuggestions(false);
            });
    }

    /**
     * Create the markup for any currently applied facet values.
     *
     * @returns JSX
     */
    function generateFacetTags() {
        const facetsData = [...searchFacetsJson];
        const selectedFacetsData: SelectedFacetData[] = [];
        let facetQueryName = '';

        // Loop over each Facet
        for (let i = 0, j = facetsData.length; i < j; ++i) {
            // Reset the facet name
            facetQueryName = '';

            // Look for a matching facet
            for (let f = 0, q = SEARCH_FACET_OPTIONS.length; f < q; ++f) {
                if (SEARCH_FACET_OPTIONS[f].name === facetsData[i].name) {
                    // Capture the facet param name
                    facetQueryName = SEARCH_FACET_OPTIONS[f].queryStringParamName;
                    break;
                }
            }

            if (facetQueryName !== '') {
                // Loop over each of the selected values (if any)
                for (let b = 0, x = facetsData[i].selectedValues.length; b < x; ++b) {
                    selectedFacetsData.push({
                        facetParamName: facetQueryName,
                        facetValue: facetsData[i].selectedValues[b].label,
                        id: `${toKebabCase(facetsData[i].selectedValues[b].label)}-${Math.ceil(Math.random() * 10000)}`,
                        label: getFacetOptionLabelByKey(facetsData[i].selectedValues[b].label),
                    });
                }
            }
        }

        return selectedFacetsData.length !== 0 ? (
            <>
                {selectedFacetsData.map((facet) => {
                    return (
                        <li key={facet.id} className="">
                            <AppliedFacetTag tagLabel={facet.label} facetParamName={facet.facetParamName} facetValue={facet.facetValue} />
                        </li>
                    );
                })}
            </>
        ) : null;
    }

    return (
        <form action={searchPageURL} method="get" role="search" onSubmit={onFormSubmit} className="search-app__container">
            <div className="grid grid-cols-4 gap-x-4 xs:grid-cols-12 sm:gap-x-6 lg:gap-x-8 xl:gap-x-10" ref={searchMainContainer}>
                {searchPageTitle ? `<h1>{searchPageTitle}</h1>` : null}
                <div className="search-app-inputs__container no-wysiwyg col-span-4 xs:col-span-12 xl:col-span-10 xl:col-start-2 xl:-ml-10 xl:-mr-10">
                    <div className="predictive-search__container" aria-live="polite" ref={searchQueryArea}>
                        <label className="body-caption-lg predictive-search__inputs-label" htmlFor="search-input">
                            {inputLabelText}
                        </label>
                        <div className="predictive-search__inputs-container">
                            <label htmlFor="search-input" className="visuallyhidden">
                                Type a search term, then press Enter to search.
                            </label>
                            <input
                                // aria-activedescendant
                                aria-autocomplete="list"
                                aria-expanded="false"
                                aria-controls="suggestions-listbox"
                                autoComplete="off"
                                className="predictive-search__input heading-size-xxl"
                                id="search-input"
                                name="query"
                                placeholder={inputPlaceholderText}
                                role="combobox"
                                spellCheck="false"
                                type="text"
                                value={userSearchTerm}
                                onChange={onSearchInputChange}
                            />

                            <button className={`open-search__button ${userSearchTerm !== '' ? 'open-search--show-clear' : ''}`} type="button" onClick={onClearSearchButtonClick}>
                                <span className="sr-only">Clear search</span>
                                <svg className="open-search__search-icon">
                                    <use xlinkHref="#search"></use>
                                </svg>
                                <div className="open-search__slash-container">
                                    <svg className="open-search__graphic-left-to-right">
                                        <use xlinkHref="#slash-forward"></use>
                                    </svg>
                                    <svg className="open-search__graphic-right-to-left">
                                        <use xlinkHref="#slash-back"></use>
                                    </svg>
                                </div>
                            </button>

                            <SearchSuggestions />
                        </div>
                        <input type="submit" className="sr-only" tabIndex={-1} value="Submit search" />
                        {inputHintText && <p className="body-default predictive-search__helper-text">{inputHintText}</p>}
                    </div>
                    <SearchFilters facetOptions={SEARCH_FACET_OPTIONS} sortOptions={SORTING_OPTIONS} hasResults={!searchHasNoResults} />
                </div>

                {/* {
                    // ! Not in use
                    showRecentSearches ?
                        <RecentSearches />
                    : null
                } */}

                <div className="search-app-results__container col-span-4 xs:col-span-12 xl:col-span-10 xl:col-start-2 xl:-ml-10 xl:-mr-10">
                    {searchResultsJson.length > 0 && !isRequestingSearchResults ? <SearchResults type={searchPageType} results={searchResultsJson} /> : null}

                    {searchResultsJson.length <= 0 && !searchHasNoResults && initialSearch ? <LoadingIndicator /> : null}

                    {searchHasNoResults && !isRequestingSearchResults ? (
                        <>
                            <h3 className="heading-size-sm">No results found</h3>
                            <p>Try searching for something more general, change the filters or check for spelling mistakes.</p>
                            <SpellingSuggestion suggestion={spellingSuggestion} />
                        </>
                    ) : null}
                </div>

                {!searchHasNoResults && !isRequestingSearchResults ? <Pagination /> : null}
            </div>

            {isRequestingSearchResults && !initialSearch ? <LoadingIndicator /> : null}
        </form>
    );
}
